/** Byte and hex helpers shared across the hashtree modules. */

export function toHex(bytes: Uint8Array): string {
  let hex = "";
  for (const byte of bytes) hex += byte.toString(16).padStart(2, "0");
  return hex;
}

export function fromHex(hex: string): Uint8Array {
  const normalized = hex.trim().toLowerCase();
  if (normalized.length % 2 !== 0) throw new Error("hex string must have an even length");
  if (!/^[0-9a-f]*$/.test(normalized)) throw new Error("hex string contains non-hex characters");
  const bytes = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < bytes.length; i++) bytes[i] = Number.parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

export function concatBytes(...chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** XOR two equal-length byte arrays. Used for BUD-18 link-private root keys. */
export function xorBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.length !== b.length) throw new Error("xor operands must be the same length");
  const out = new Uint8Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = a[i]! ^ b[i]!;
  return out;
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  // `data.buffer` may be a larger pooled ArrayBuffer, so pass an exact-length copy.
  const digest = await crypto.subtle.digest("SHA-256", data.slice());
  return new Uint8Array(digest);
}

export async function sha256Hex(data: Uint8Array): Promise<string> {
  return toHex(await sha256(data));
}

const UNITS = ["B", "KiB", "MiB", "GiB"];

/** Human-readable byte size, e.g. `2 MiB`, `1.5 KiB`, `843 B`. */
export function formatBytes(count: number): string {
  let value = count;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const rounded = unit === 0 || Number.isInteger(value) ? value.toString() : value.toFixed(value < 10 ? 2 : 1);
  return `${rounded} ${UNITS[unit]}`;
}
